import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Contact, Info, Users, type LucideIcon } from 'lucide-react';
import { Skeleton } from '../Skeleton';
import type { HiringLink, HiringUnavailableReason } from '../../services/api';
import {
  hiringOpeningAPI,
  type OpeningDetails,
  type OpeningTeam,
  type OpeningTeamPerson,
  type OpeningTeamRound,
} from '../../services/hiringOpeningApi';
import { InfoDot, KeyValues } from './HiringForm';
import { HOUSE_TABS, houseTabClass } from './hiringLooks';
import { OpeningCandidates } from './OpeningCandidates';
import {
  DELETED_WORDS,
  HIRING_CODES,
  OPENING_WORDS,
  RAISE_WORDS,
  createdWords,
  deletedSeenLine,
  deletedWhereLine,
  filledByLine,
  hiringCodeOf,
  hiringErrorText,
  isHiringOff,
  messageStatusSentence,
  openingInfoLines,
  openingPill,
  openingStoreWords,
  publishedWords,
  reasonSentence,
  stageCountsTotal,
  stoppedByLine,
} from './hiringText';

/**
 * One followed opening, as the approved mock-up draws it (15 Oct 2026, panel 2): ONE status
 * pill, one row of four big numbers (Positions, Filled, Offered, Open) with ONE (i) holding
 * every caveat, and three tabs - Details · Team · Candidates (n).
 *
 *   Details     Store, Address, City, State, PIN, Published, Created; the opening's history
 *               one click away under them.
 *   Team        Recruiters, Offer makers, and each round's interviewer (a client round says
 *               "Client", with the name only for a holder of "Raise & cancel hiring").
 *   Candidates  for everyone who may see hiring (27 Oct 2026; until then a holder of "Raise
 *               & cancel hiring" only): a name search, a stage filter and a table (Name,
 *               Mobile, From, Source, Stage, Round, Applied), 10 at a time with "Show more".
 *
 * DELETED IN RECRUITMENT (21 Oct 2026). An opening a good read of its budget's list did not
 * find was deleted there (1Office's list leaves out nothing else): in place of the numbers and
 * the tabs, one plain notice (OpeningDeleted), and nothing of it is read from Recruitment.
 *
 * Each tab is read from the server only when it is first opened, once (the Details when the
 * opening is shown). Recruitment's read of an opening is costly to 1Office; the server keeps
 * it a minute and limits how many run at once, and nothing here reads it again by itself.
 * Names and mobile numbers are shown, never kept: not in the page's address, a log, analytics
 * or the browser's storage.
 */

type ReadState<T> = { state: 'idle' | 'loading' | 'ready' | 'failed'; data: T | null; error: string | null };
const IDLE = { state: 'idle', data: null, error: null } as const;

/** A link whose opening can be read: followed, with an opening number. */
export const openingReadable = (link: HiringLink): boolean => link.state === 'live' && link.openingId != null;

/**
 * One read of the opening (details or team), made when `wanted` first turns true and again
 * only with `retry`. A refusal that says hiring is off goes to `onHiringGone`; one that says
 * the opening is not in its budget's list (it was deleted in Recruitment) to `onNotListed`.
 */
function useOpeningRead<T>(
  wanted: boolean,
  key: string,
  read: (signal: AbortSignal) => Promise<{ data?: { data?: T } } | undefined>,
  fallback: string,
  onHiringGone: () => void,
  onNotListed?: () => void,
): ReadState<T> & { retry: () => void } {
  const [st, setSt] = useState<ReadState<T>>(IDLE);
  const [reads, setReads] = useState(0);
  const readNow = useRef(read);
  readNow.current = read;
  const gone = useRef(onHiringGone);
  gone.current = onHiringGone;
  const notListed = useRef(onNotListed);
  notListed.current = onNotListed;
  const done = useRef<string | null>(null);

  useEffect(() => {
    const id = `${key}#${reads}`;
    if (!wanted) {
      // No longer readable (the link was stopped or cancelled): a read that never finished says nothing.
      setSt((was) => (was.state === 'loading' ? IDLE : was));
      return undefined;
    }
    if (done.current === id) return undefined;
    done.current = id;
    const control = typeof AbortController === 'function' ? new AbortController() : null;
    let cancelled = false;
    setSt({ state: 'loading', data: null, error: null });
    Promise.resolve()
      .then(() => readNow.current(control?.signal as AbortSignal))
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data ?? null;
        setSt(data ? { state: 'ready', data, error: null } : { state: 'failed', data: null, error: fallback });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (isHiringOff(err)) {
          gone.current();
          return;
        }
        setSt({ state: 'failed', data: null, error: hiringErrorText(err, fallback) });
        if (hiringCodeOf(err) === HIRING_CODES.notInBudget) notListed.current?.();
      });
    return () => {
      cancelled = true;
      // Read again if the opening is shown again: this read was not finished.
      done.current = null;
      control?.abort();
    };
  }, [wanted, key, reads, fallback]);

  return { ...st, retry: () => setReads((n) => n + 1) };
}

/**
 * The opening's details, read once it is shown. Null for a link that is not followed or has no
 * opening yet, and not read while `skip` (the link says the opening was deleted in Recruitment).
 */
export function useOpeningDetails(projectId: number, link: HiringLink, onHiringGone: () => void, skip = false) {
  return useOpeningRead<OpeningDetails>(
    openingReadable(link) && !skip,
    `${projectId}:${link.id}`,
    (signal) => hiringOpeningAPI.details(projectId, link.id, signal),
    OPENING_WORDS.failed,
    onHiringGone,
  );
}

/** The one pill: a dot and a word; why it needs attention on hover. `deleted`: the window's own read found the opening deleted. */
export function OpeningStatusPill({ link, deleted = false }: { link: HiringLink; deleted?: boolean }) {
  const pill = openingPill(link, deleted);
  return (
    <span
      title={pill.hover ?? undefined}
      className={`inline-flex items-center whitespace-nowrap rounded-full py-0.5 pl-2 pr-2.5 text-xs font-medium ${pill.className}`}
    >
      <span aria-hidden="true" className={`mr-1.5 inline-block h-2 w-2 rounded-full ${pill.dot}`} />
      {pill.text}
    </span>
  );
}

/** The four numbers in one row, Open in the house colour, and the one (i). */
export function OpeningNumbers({ link, details }: { link: HiringLink; details: OpeningDetails | null }) {
  const numbers = details?.numbers ?? link.numbers;
  if (!numbers) return null;
  const reason = openingReadReason(details);
  const lines = openingInfoLines({
    link,
    readAt: details?.recruitment.readAt ?? null,
    numbersFrom: details?.numbersFrom ?? null,
    reason,
    sharedWith: details?.sharedWith ?? null,
  });
  // What a message from Recruitment told beyond the numbers (a joining date, who joined).
  const told = messageStatusSentence(link);
  if (told) lines.unshift(told);
  const figures: Array<{ label: string; value: number; high?: boolean }> = [
    { label: OPENING_WORDS.numbers.positions, value: numbers.positions },
    { label: OPENING_WORDS.numbers.filled, value: numbers.filled },
    { label: OPENING_WORDS.numbers.offered, value: numbers.offered },
    { label: OPENING_WORDS.numbers.open, value: numbers.open, high: true },
  ];
  return (
    <div className="grid grid-cols-[repeat(4,minmax(0,1fr))_auto] rounded-lg border border-gray-200">
      <dl className="contents">
        {figures.map((figure, i) => (
          // The figure is drawn above its label; in the markup the label still comes first.
          <div key={figure.label} className={`flex flex-col-reverse px-3.5 py-3 ${i > 0 ? 'border-l border-gray-200' : ''}`}>
            <dt className="text-xs text-gray-500">{figure.label}</dt>
            <dd className={`text-2xl font-semibold leading-tight tabular-nums ${figure.high ? 'text-primary-600' : 'text-gray-900'}`}>{figure.value}</dd>
          </div>
        ))}
      </dl>
      <div className="flex items-start pr-2.5 pt-2.5">
        <InfoDot text={lines.join(' ')} label={OPENING_WORDS.numbersInfo} />
      </div>
    </div>
  );
}

/** Why Recruitment could not be read for the details, in one line; null when it was. */
const openingReadReason = (details: OpeningDetails | null): string | null => {
  if (!details || details.recruitment.read === 'ok') return null;
  const why = details.recruitment.reason;
  if (why === 'busy') return 'Recruitment is busy. Try again in a minute.';
  // Only an opening ChannelPlex raised that the list has not shown yet gets this far: any other
  // opening its own read finds not listed is drawn as deleted in Recruitment (OpeningDeleted).
  if (why === 'not_listed') return `${RAISE_WORDS.notInListTitle}.`;
  return reasonSentence((why ?? 'unreachable') as HiringUnavailableReason);
};

const personWords = (person: OpeningTeamPerson): string => String(person.name ?? '').trim() || OPENING_WORDS.nobodyOnIt;
const peopleWords = (people: readonly OpeningTeamPerson[]): string =>
  (people.length > 0 ? people.map(personWords).join(', ') : OPENING_WORDS.none);
const roundWords = (round: OpeningTeamRound): ReactNode => {
  if (round.kind === 'client') {
    return round.name ? <>{round.name} <span className="text-gray-500">· {OPENING_WORDS.client}</span></> : OPENING_WORDS.client;
  }
  if (round.kind === 'interviewer') return String(round.name ?? '').trim() || OPENING_WORDS.none;
  return OPENING_WORDS.none;
};

function Loading({ rows = 4 }: { rows?: number }) {
  return (
    <div role="status" aria-label={OPENING_WORDS.loading} className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="grid grid-cols-[110px_1fr] gap-4">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-full" />
        </div>
      ))}
    </div>
  );
}

export function ReadFailed({ error, onRetry }: { error: string | null; onRetry: () => void }) {
  return (
    <div className="space-y-1.5 text-sm">
      <p role="alert" className="text-gray-700">{error ?? OPENING_WORDS.failed}</p>
      <button type="button" onClick={onRetry} className="text-[13px] font-medium text-primary-600 hover:underline">{OPENING_WORDS.tryAgain}</button>
    </div>
  );
}

/**
 * An opening deleted in Recruitment, in place of its numbers and its tabs: one plain notice,
 * the store and where the opening was on one line, and when it was last checked (or seen) in
 * small grey. `byStatus`: the link's status says so; otherwise the window's own read found it.
 * 16px under it, as the tabs leave (their panel's py-4), before a card's foot; 32px under a
 * window's pinned foot (`underFoot`), which overlaps the last 16px of the body. Padding, not a
 * margin: the card's space-y sets its children's bottom margins to 0.
 */
export function OpeningDeleted({ link, byStatus, underFoot = false }: { link: HiringLink; byStatus: boolean; underFoot?: boolean }) {
  const where = deletedWhereLine(link);
  const seen = deletedSeenLine(link, byStatus);
  return (
    <div className={underFoot ? 'pb-8' : 'pb-4'}>
      <div role="note" aria-label={DELETED_WORDS.badge} className="space-y-1 rounded-lg border border-gray-200 px-4 py-3">
        <p className="text-sm text-gray-900">{DELETED_WORDS.notice}</p>
        {where && <p className="text-[13px] text-gray-700">{where}</p>}
        {seen && <p className="text-xs text-gray-500">{seen}</p>}
      </div>
    </div>
  );
}

type Tab = 'details' | 'team' | 'candidates';

/** The icon before each tab's name: the house tab strip (StoreMasterPage). */
const TAB_ICON: Record<Tab, LucideIcon> = { details: Info, team: Users, candidates: Contact };

export function OpeningTabs({
  projectId, link, details, detailsState, detailsError, onRetryDetails, historyShown, onHistory, history, extra, onHiringGone, onNotListed,
}: {
  projectId: number;
  link: HiringLink;
  details: OpeningDetails | null;
  detailsState: 'idle' | 'loading' | 'ready' | 'failed';
  detailsError: string | null;
  onRetryDetails: () => void;
  /** Whether the opening's history is open under Details, and how to open or close it. */
  historyShown: boolean;
  onHistory: (shown: boolean) => void;
  /** The opening's history (its timeline), drawn under Details while it is open. */
  history: ReactNode;
  /** Drawn at the end of Details (a store's people leaving). */
  extra?: ReactNode;
  onHiringGone: () => void;
  /** A read of the team or the candidates was refused: the opening is not in its budget's list (deleted in Recruitment). */
  onNotListed?: () => void;
}) {
  const [tab, setTab] = useState<Tab>('details');
  const [seen, setSeen] = useState<Record<Tab, boolean>>({ details: true, team: false, candidates: false });
  const [candidatesAll, setCandidatesAll] = useState<number | null>(null);
  const readable = openingReadable(link);
  const team = useOpeningRead<OpeningTeam>(
    readable && seen.team,
    `${projectId}:${link.id}`,
    (signal) => hiringOpeningAPI.team(projectId, link.id, signal),
    OPENING_WORDS.failed,
    onHiringGone,
    onNotListed,
  );
  const open = (next: Tab) => {
    setTab(next);
    setSeen((was) => (was[next] ? was : { ...was, [next]: true }));
  };
  const tabs: Array<{ id: Tab; label: string }> = readable
    ? [
      { id: 'details', label: OPENING_WORDS.tabs.details },
      { id: 'team', label: OPENING_WORDS.tabs.team },
      { id: 'candidates', label: OPENING_WORDS.candidatesTab(candidatesAll ?? stageCountsTotal(link.stages)) },
    ]
    : [{ id: 'details', label: OPENING_WORDS.tabs.details }];
  const tabId = (id: Tab) => `opening-${link.id}-${id}`;

  const linked = link.address?.now ?? link.address?.linked ?? null;
  const subjectStore = link.subject.kind === 'store' ? link.subject : null;
  const store = details ? details.store : subjectStore;
  const created = createdWords(details?.created ?? { on: link.raisedAt, byCode: link.raisedBy, byName: null });
  const ended = link.state === 'closed' ? filledByLine(link) ?? stoppedByLine(link) : null;
  const none = <span className="text-gray-400">{OPENING_WORDS.none}</span>;
  const value = (text: string | null | undefined): ReactNode => (text && String(text).trim() ? text : none);

  return (
    <div>
      {/* The house tab strip (StoreMasterPage): an icon, the name, and the candidates' count in brackets. */}
      <div role="tablist" aria-label={OPENING_WORDS.tabsLabel} className={`mt-4 ${HOUSE_TABS.tray}`}>
        {tabs.map((t) => {
          const Icon = TAB_ICON[t.id];
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              id={`${tabId(t.id)}-tab`}
              aria-selected={tab === t.id}
              aria-controls={tabId(t.id)}
              onClick={() => open(t.id)}
              className={houseTabClass(tab === t.id)}
            >
              <Icon aria-hidden="true" className={HOUSE_TABS.icon} />
              {t.label}
            </button>
          );
        })}
      </div>

      <div role="tabpanel" id={tabId(tab)} aria-labelledby={`${tabId(tab)}-tab`} className="min-h-[280px] py-4">
        {tab === 'details' && (
          <div className="space-y-4">
            {detailsState === 'loading' ? (
              <Loading rows={7} />
            ) : (
              <KeyValues
                label={OPENING_WORDS.tabs.details}
                rows={[
                  { key: 'store', label: OPENING_WORDS.details.store, value: openingStoreWords(store) },
                  { key: 'address', label: OPENING_WORDS.details.address, value: value(details?.address.location ?? linked?.location) },
                  { key: 'city', label: OPENING_WORDS.details.city, value: value(details?.address.cityName ?? linked?.cityName) },
                  { key: 'state', label: OPENING_WORDS.details.state, value: value(details?.address.stateName) },
                  { key: 'pin', label: OPENING_WORDS.details.pin, value: value(details?.address.pincode) },
                  { key: 'published', label: OPENING_WORDS.details.published, value: value(publishedWords(details?.published)) },
                  { key: 'created', label: OPENING_WORDS.details.created, value: value(created) },
                ]}
              />
            )}
            {detailsState === 'failed' && <ReadFailed error={detailsError} onRetry={onRetryDetails} />}
            {ended && <p className="text-xs text-gray-500">{ended}</p>}
            {extra}
            <div>
              <button
                type="button"
                aria-expanded={historyShown}
                onClick={() => onHistory(!historyShown)}
                className="text-[13px] font-medium text-primary-600 hover:underline"
              >
                {historyShown ? OPENING_WORDS.hideHistory : OPENING_WORDS.history}
              </button>
              {historyShown && history}
            </div>
          </div>
        )}
        {tab === 'team' && (
          team.state === 'failed' ? <ReadFailed error={team.error} onRetry={team.retry} />
            : !team.data ? <Loading rows={3} />
              : (
                <KeyValues
                  label={OPENING_WORDS.tabs.team}
                  rows={[
                    { key: 'recruiters', label: OPENING_WORDS.team.recruiters, value: peopleWords(team.data.recruiters) },
                    { key: 'offer', label: OPENING_WORDS.team.offerMakers, value: peopleWords(team.data.offerMakers) },
                    ...team.data.rounds.map((round) => ({ key: `r${round.round}`, label: OPENING_WORDS.team.round(round.round), value: roundWords(round) })),
                  ]}
                />
              )
        )}
        {/* Mounted once opened, and kept: its search and its pages survive a look at another tab. */}
        {seen.candidates && readable && (
          <div hidden={tab !== 'candidates'}>
            <OpeningCandidates projectId={projectId} linkId={link.id} onCounts={setCandidatesAll} onHiringGone={onHiringGone} onNotListed={onNotListed} />
          </div>
        )}
      </div>
    </div>
  );
}
